import React, { useEffect, useMemo, useState } from "react";
import { Alert, Collapse, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import {
  fetchTableBookingLayout,
  fetchTableBookingLayouts,
} from "../../../../api/tableBookingApi";
import CustomToggleButton from "../../../customToggleButton/customToggleButton";
import {
  flattenLayoutPricingRows,
  formatEffectivePriceHint,
  formatEffectivePricePlaceholder,
  indexAreaPricingRows,
  MANUAL_AVAILABILITY_VALUES,
  normalizeLayoutForEventPricing,
  normalizeManualAvailability,
  pricingOverrideKey,
  resolveEffectiveEventPricing,
  resolveEventChannelSoldOut,
} from "../../../../helpers/eventTableBooking";
import {
  layoutRecordId,
  manualAvailabilityButtonLabel,
  normalizeLayoutDetail,
  normalizeLayoutsList,
} from "../../../../pages/tableBooking/tableBookingUtils";
import "./eventTableBooking.css";

const EventTableBookingPreview = ({ clubId, values }) => {
  const { t } = useTranslation();
  const enabled = Boolean(values?.tableBookingEnabled);
  const layoutId = String(values?.tableBookingLayoutId ?? "").trim();
  const slotKey = String(values?.tableBookingSlotKey ?? "").trim();
  const areaPricing = useMemo(
    () =>
      Array.isArray(values?.tableBookingAreaPricing)
        ? values.tableBookingAreaPricing
        : [],
    [values]
  );
  const [layouts, setLayouts] = useState([]);
  const [layoutDetail, setLayoutDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [expandedZoneIds, setExpandedZoneIds] = useState(() => new Set());

  useEffect(() => {
    if (!enabled || !clubId) {
      setLayouts([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    fetchTableBookingLayouts(clubId)
      .then((res) => {
        if (!cancelled) setLayouts(normalizeLayoutsList(res));
      })
      .catch((err) => {
        if (cancelled) return;
        setLayouts([]);
        setLoadError(
          typeof err === "string"
            ? err
            : err?.message || t("TABLE_BOOKING_LAYOUTS_LOAD_ERROR")
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, clubId, t]);

  useEffect(() => {
    if (!enabled || !clubId || !layoutId) {
      setLayoutDetail(null);
      return undefined;
    }
    const listMatch = layouts.find((layout) => layoutRecordId(layout) === layoutId);
    let cancelled = false;
    fetchTableBookingLayout(clubId, layoutId)
      .then((res) => {
        if (cancelled) return;
        const detail = normalizeLayoutDetail(res);
        setLayoutDetail(normalizeLayoutForEventPricing(detail || listMatch) || null);
      })
      .catch(() => {
        if (!cancelled) {
          setLayoutDetail(normalizeLayoutForEventPricing(listMatch) || null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, clubId, layoutId, layouts]);

  const selectedLayout = layoutDetail;
  const pricingByZone = useMemo(() => {
    const rows = flattenLayoutPricingRows(selectedLayout);
    const zones = rows.filter((row) => row.kind === "zone");
    const tables = rows.filter((row) => row.kind === "table");
    return zones.map((zone) => ({
      ...zone,
      tables: tables.filter((table) => table.areaId === zone.areaId),
    }));
  }, [selectedLayout]);

  const zoneIdsKey = pricingByZone.map((zone) => zone.areaId).join(",");
  const firstZoneId = pricingByZone[0]?.areaId ?? "";

  useEffect(() => {
    setExpandedZoneIds(firstZoneId ? new Set([firstZoneId]) : new Set());
  }, [layoutId, zoneIdsKey, firstZoneId]);

  const overrideIndex = useMemo(
    () => indexAreaPricingRows(areaPricing),
    [areaPricing]
  );

  const slotLabel = useMemo(() => {
    const slots = Array.isArray(selectedLayout?.slots) ? selectedLayout.slots : [];
    const match = slots.find((slot) => String(slot?.key ?? "") === slotKey);
    return String(match?.label || match?.key || slotKey || "").trim();
  }, [selectedLayout, slotKey]);

  const layoutLabel = selectedLayout
    ? `${selectedLayout.title || t("TABLE_BOOKING_LAYOUT_EDIT")} (${
        selectedLayout.viewkey || layoutRecordId(selectedLayout)
      })`
    : values?.tableBookingViewkey || "";

  const toggleZone = (areaId) => {
    setExpandedZoneIds((current) => {
      const next = new Set(current);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  const renderFields = (row) => {
    const override = overrideIndex.get(
      row.tableId ? pricingOverrideKey(row.areaId, row.tableId) : row.areaId
    );
    const effective = resolveEffectiveEventPricing(row, overrideIndex);
    const layoutDefault = row.layoutDefault || {};
    const online = resolveEventChannelSoldOut(row, overrideIndex, "onlineSoldOut");
    const offline = resolveEventChannelSoldOut(row, overrideIndex, "offlineSoldOut");
    const availability = normalizeManualAvailability(
      override?.manualAvailability ?? layoutDefault.manualAvailability
    );
    const priceValue =
      override?.priceFromPerPerson != null && override.priceFromPerPerson !== ""
        ? String(override.priceFromPerPerson)
        : formatEffectivePricePlaceholder(effective, layoutDefault, t);
    const currency = String(
      override?.currency || layoutDefault.currency || effective?.currency || "INR"
    );
    const minValue =
      override?.minPartySize != null && override.minPartySize !== ""
        ? String(override.minPartySize)
        : layoutDefault.minPartySize != null
          ? String(layoutDefault.minPartySize)
          : "";
    const maxValue =
      override?.maxPartySize != null && override.maxPartySize !== ""
        ? String(override.maxPartySize)
        : layoutDefault.maxPartySize != null
          ? String(layoutDefault.maxPartySize)
          : "";
    const description =
      String(override?.description || layoutDefault.description || "").trim();

    return (
      <div className="etb-fields">
        <div className="etb-price-grid">
          <div className="etb-field">
            <label className="etb-field-label">
              {t("EVENT_TABLE_BOOKING_COL_EVENT_PRICE")}
            </label>
            <Form.Control readOnly className="etb-input" value={priceValue} />
          </div>
          <div className="etb-field etb-field-currency">
            <label className="etb-field-label">{t("TABLE_BOOKING_CURRENCY")}</label>
            <Form.Control readOnly className="etb-input" value={currency} />
          </div>
        </div>
        <div className="etb-meta-grid">
          <div className="etb-field">
            <label className="etb-field-label">{t("TABLE_BOOKING_MIN_PARTY")}</label>
            <Form.Control readOnly className="etb-input" value={minValue} />
          </div>
          <div className="etb-field">
            <label className="etb-field-label">{t("TABLE_BOOKING_MAX_PARTY")}</label>
            <Form.Control readOnly className="etb-input" value={maxValue} />
          </div>
          <div className="etb-field etb-field-availability">
            <label className="etb-field-label">{t("TABLE_BOOKING_MANUAL_AVAIL")}</label>
            <div className="etb-avail-group">
              {MANUAL_AVAILABILITY_VALUES.map((key) => (
                <button
                  key={key}
                  type="button"
                  tabIndex={-1}
                  className={`etb-avail-btn is-${key.toLowerCase()} ${
                    availability === key ? "is-active" : ""
                  }`}
                >
                  {manualAvailabilityButtonLabel(key, t)}
                </button>
              ))}
            </div>
          </div>
          <div className="etb-field etb-field-channel-soldout">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_ONLINE_SOLD_OUT")}
            </label>
            <CustomToggleButton
              id={`preview-online-${row.areaId}-${row.tableId || "zone"}`}
              checked={online.checked}
              disabled
            />
          </div>
          <div className="etb-field etb-field-channel-soldout">
            <label className="etb-field-label">
              {t("TABLE_BOOKING_OFFLINE_SOLD_OUT")}
            </label>
            <CustomToggleButton
              id={`preview-offline-${row.areaId}-${row.tableId || "zone"}`}
              checked={offline.checked}
              disabled
            />
          </div>
          <div className="etb-field etb-field-wide">
            <label className="etb-field-label">
              {t("EVENT_TABLE_BOOKING_DESCRIPTION")}
            </label>
            <Form.Control
              readOnly
              as="textarea"
              rows={2}
              className="etb-input"
              value={description}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderZone = (zone) => {
    const isExpanded = expandedZoneIds.has(zone.areaId);
    const effective = resolveEffectiveEventPricing(zone, overrideIndex);
    return (
      <article
        className={`etb-zone-card ${isExpanded ? "is-expanded" : "is-collapsed"}`}
        key={zone.areaId}
      >
        <div className="etb-zone-accent" aria-hidden="true" />
        <div className="etb-zone-body">
          <button
            type="button"
            className="etb-zone-toggle"
            onClick={() => toggleZone(zone.areaId)}
            aria-expanded={isExpanded}
          >
            <span className="etb-zone-chevron" aria-hidden="true">
              <i
                className={`mdi ${
                  isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
                }`}
              />
            </span>
            <span className="etb-zone-toggle-main">
              <span className="etb-card-title">{zone.label}</span>
              {!isExpanded && zone.tables.length > 0 ? (
                <span className="etb-card-sub">
                  {t("EVENT_TABLE_BOOKING_NESTED_TABLE_COUNT", {
                    count: zone.tables.length,
                  })}
                </span>
              ) : null}
            </span>
            <span className="etb-zone-toggle-pill">
              <span className="etb-layout-pill">
                {formatEffectivePriceHint(effective, zone.layoutDefault)}
              </span>
            </span>
          </button>
          <Collapse in={isExpanded}>
            <div className="etb-zone-collapse-body">
              <section className="etb-zone-main">{renderFields(zone)}</section>
              {zone.tables.length > 0 ? (
                <section className="etb-tables-branch">
                  <div className="etb-tables-list">
                    {zone.tables.map((table) => {
                      const tablePrice = resolveEffectiveEventPricing(
                        table,
                        overrideIndex
                      );
                      return (
                        <div className="etb-table-card" key={table.tableId}>
                          <div className="etb-card-head">
                            <div className="etb-card-head-left">
                              <div className="etb-table-title-row">
                                <span className="etb-table-icon" aria-hidden="true">
                                  <i className="mdi mdi-table-furniture" />
                                </span>
                                <div>
                                  <p className="etb-card-title mb-0">{table.label}</p>
                                  <p className="etb-card-sub mb-0">
                                    {t("EVENT_TABLE_BOOKING_NESTED_TABLE")} ·{" "}
                                    {table.zoneLabel}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <span className="etb-layout-pill">
                              {formatEffectivePriceHint(
                                tablePrice,
                                table.layoutDefault
                              )}
                            </span>
                          </div>
                          {renderFields(table)}
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}
            </div>
          </Collapse>
        </div>
      </article>
    );
  };

  if (!enabled) {
    return <p className="event-form-preview-value">{t("EVENT_PREVIEW_NO")}</p>;
  }

  return (
    <div className="etb-root event-preview-tables">
      {loading ? <p className="etb-loading">{t("EVENT_TABLE_BOOKING_LOADING")}</p> : null}
      {loadError ? (
        <Alert variant="danger" className="py-2 small etb-alert">
          {loadError}
        </Alert>
      ) : null}
      <div className="etb-settings-card">
        <p className="etb-settings-title">{t("EVENT_TABLE_BOOKING_LAYOUT_SETTINGS")}</p>
        <p className="etb-settings-label mb-1">{t("EVENT_TABLE_BOOKING_LAYOUT")}</p>
        <p className="etb-card-title">{layoutLabel || "—"}</p>
        <p className="etb-settings-label mb-1 mt-2">{t("TABLE_BOOKING_SLOT_LABEL")}</p>
        <p className="etb-card-sub">{slotLabel || "—"}</p>
      </div>
      {pricingByZone.length ? (
        <div className="etb-zone-list">{pricingByZone.map(renderZone)}</div>
      ) : !loading ? (
        <Alert variant="warning" className="py-2 small etb-alert">
          {t("EVENT_TABLE_BOOKING_NO_AREAS")}
        </Alert>
      ) : null}
    </div>
  );
};

export default EventTableBookingPreview;
